import { useEffect, useRef, useState } from "react";
import { BRAND } from "@core/brand/brand-config";
import { store } from "../../../redux/store";
import axios from "axios";
import { getApiUrl } from "@utils/baseUrl";
import Button from "@components/base/button";
import { toast } from "react-toastify";
import { getTitleCase, useDebounce } from "@utils/utils";
import LoaderImg from "@assets/images/loader.gif";
import ReactCountryFlag from "react-country-flag";
import { globalCountryDetailsDictionary } from "@core/global/global-country-details";
import { AUTOSUGGEST_API_MAP, BRANCHES, FORMORDER, LIMIT_ON_SELECTION, LINE_OF_BUSINESS_MAP, LOB_ENUM, LOB_LABEL_MAP, PRE_FETCH_API_MAPS, SLICE_MAPS, STEP_FIELD_MAPS } from "./utils";
import LoadingSkeleton from "@shared/LoadingSkeleton";
import sparkleImg from "../../../assets/images/sparkle.svg";
import { updateOnboardingStatus } from "../onboarding-screens/onboarding/utils";
import Modal from "../onboarding-screens/Modal";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { useHistory } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import { useSelector } from "react-redux";
import { countryDetails } from "../../../core/dictionary/global";
import { useAnalytics, createEventData } from "@core/analytics";
import DropDownOnSearch from "./DropDownOnSearch";
import UserCompanyProfileCardView from "./UserCompanyProfileCardView";

const ProductOnboarding = ({ setShow }) => {
  const [data, setData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [currentData, setCurrentData] = useState({});
  const [formValues, setFormValues] = useState({});
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const [dropDownData, setDropDownData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [sugestedData, setSuggestedData] = useState({});
  const [input, setInput] = useState({});
  const [subBranchKey, setSubBranchKey] = useState("");
  const [activeFieldKey, setActiveFieldKey] = useState("");
  const debouncedInputValue = useDebounce(input, 300);
  const [checkedOptions, setCheckedOptions] = useState({});
  const [companyData, setCompanyData] = useState({ found: false, slug: null, companyId: null });
  const [isBasicPlan, setIsBasicPlan] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const userData = store.getState().userDetails.userData;
  const dropDownRefs = useRef({});
  const [maxStepAvailable, setMaxStepAvailable] = useState(1);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const history = useHistory();
  const [showAllTags, setShowAllTags] = useState({});
  const [userCompanyProfiles, setUserCompanyProfiles] = useState([]);
  const [userCompanyProfilesFetched, setUserCompanyProfilesFetched] = useState(false);
  const [companySearchText, setCompanySearchText] = useState("");
  const [isInitialUserCompanySearchDone, setIsInitialUserCompanySearchDone] = useState(false);
  const [showCompanySearchInput, setShowCompanySearchInput] = useState(false);
  const creditDetails = useSelector((state) => state.userDetails.creditDetails);
  const userCredit = creditDetails?.data?.find((item) => item?.is_active) ?? creditDetails?.data?.[0] ?? {};
  const filteredDataset = creditDetails?.data?.filter((item) => item?.product_id !== "trademo_MC" && item.navigate !== "upgrade") || [];
  // navigate "upgrade" marks an upsell entry rather than an entitlement, so exclude it
  // here as filteredDataset does above - otherwise an account that is only being offered
  // the global plan would be routed to the global endpoints it cannot use.
  const subscribedDatasets = (creditDetails?.data ?? []).filter((item) => item?.is_active && item?.navigate !== "upgrade");
  const isGlobalPlan = subscribedDatasets.some((item) => item?.product_id === "trademo_GLOBAL");
  const [latestArrivalDate, setLatestArrivalDate] = useState("");
  // userCredit is one arbitrary row - find() on a multi-country account picks whichever
  // country happens to be first, and that row's date fields are often blank. The dates
  // that do exist live on other rows, so take the earliest across all of them.
  const earliestSearchFromDate = subscribedDatasets
    .flatMap((item) => [item?.searchFromDate, item?.defaultSearchFromDate])
    .filter(Boolean)
    .sort()[0];
  const isMirrorSubscribed = filteredDataset.some((dataset) => dataset?.is_active && countryDetails[dataset?.product_id]?.name?.toLowerCase()?.includes("mirror"));
  const isDirectSubscribed = filteredDataset.some((dataset) => dataset?.is_active && !countryDetails[dataset?.product_id]?.name?.toLowerCase()?.includes("mirror"));
  const { trackEvent, setUser } = useAnalytics();

  // toDate is the dataset's latest arrival date. Global plans must ask the global
  // endpoint - the multicountry one 400s for them.
  useEffect(() => {
    if (!creditDetails?.data?.length) return;
    const arrivalEndpoint = isGlobalPlan ? "intelSearchGlobalLatestArrivalDate" : "intelSearchMultiLatestArrivalDate";
    axios
      .get(getApiUrl(arrivalEndpoint))
      .then((res) => setLatestArrivalDate(res?.data?.latest_actual_arrival_date || ""))
      .catch(() => setLatestArrivalDate(""));
  }, [creditDetails?.data?.length, isGlobalPlan]);
  const email = userData.email;

  const PERSONA_INFO_STEP_INDEX = Number(Object.entries(FORMORDER).find(([, value]) => value === "PERSONA_INFO")?.[0] ?? -1);
  const [showCloseConfirmationModal, setShowCloseConfirmationModal] = useState(false);
  const [closeConfirmationStep, setCloseConfirmationStep] = useState(null);

  const closeOnboardingPopup = (stepToTrack = currentStep) => {
    setShow(false);
    updateOnboardingStatus("show_onboarding_popup", false);
    trackEvent(
      `onboarding_close_step_${stepToTrack}`,
      createEventData("Onboarding-Personalisation", {
        url: window.location.pathname,
      })
    );
    setUser(email);
  };

  const handleCloseIconClick = () => {
    if (currentStep > PERSONA_INFO_STEP_INDEX) {
      setCloseConfirmationStep(currentStep);
      setShowCloseConfirmationModal(true);
      return;
    }

    closeOnboardingPopup(currentStep);
  };

  const confirmCloseOnboardingPopup = () => {
    setShowCloseConfirmationModal(false);
    closeOnboardingPopup(closeConfirmationStep ?? currentStep);
    setCloseConfirmationStep(null);
    updateOnboardingStatus("line_of_business", formValues.line_of_business?.label);
  };

  const toggleUserCompanyProfileSelection = (profile) => {
    setFormValues((prevForm) => ({
      ...prevForm,
      user_company_profile: profile,
    }));
  };

  const getCurrentFieldKeys = () => {
    const currentStepKey = FORMORDER[currentStep];
    if (!currentStepKey) return [];

    const fields = STEP_FIELD_MAPS[currentStepKey];

    if (Array.isArray(fields)) {
      return fields;
    } else if (typeof fields === "object" && subBranchKey) {
      return fields[subBranchKey] || [];
    }

    return [];
  };

  const actionPostOnboardingCompletion = async () => {
    await axios.post(getApiUrl("user_onboarding/"), {
      page: "FOUNDATIONAL_INFO",
      input_details: {
        company_name: "Samsung",
      },
    });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!activeFieldKey) return;

      const dropdownContainer = dropDownRefs.current[activeFieldKey];
      const dropdownElement = document.getElementById("suggestions-dropdown") || document.getElementById("extra-items");

      if (dropdownContainer && !dropdownContainer.contains(e.target) && dropdownElement && !dropdownElement.contains(e.target)) {
        setDropDownOpen(false);
        setIsLoadingSuggestions(false);
        setShowAllTags({ ...showAllTags, [activeFieldKey]: false });
        if (input[activeFieldKey] === "") {
          setDropDownData([]);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropDownOpen, activeFieldKey]);

  useEffect(() => {
    // Guard clauses
    if (!activeFieldKey || !Array.isArray(sugestedData[activeFieldKey])) return;

    const loadingProductItems = sugestedData[activeFieldKey].filter((item) => item?.from === "product_description" && item?.isloading === true);

    if (loadingProductItems.length === 0) return;

    const fetchHSCodesForItem = async (item) => {
      try {
        const response = await axios.post(getApiUrl("postSearchRecomendation"), {
          user_search: item.key,
          category: "product",
        });

        const hsInfo = response.data?.hs_info || {};
        const userSearch = response.data?.user_search || item.key;

        const updatedItem = {
          key: hsInfo.hs_code || item.key,
          description: getTitleCase(userSearch),
          issuggested: false,
          isloading: false,
          from: "product_description",
          company_id: item?.hashed_id && item?.hashed_id !== "" ? item.hashed_id : item?._id && item?._id !== "" ? item._id : (item.mongo_id ?? ""),
          isBookmarked: true,
        };

        setSuggestedData((prev) => {
          const currentList = prev[activeFieldKey] || [];
          const updatedList = Array.isArray(currentList) && currentList?.length > 0 ? currentList.map((el) => (el.key === item.key ? updatedItem : el)) : [];
          return { ...prev, [activeFieldKey]: updatedList };
        });
        setFormValues((prev) => {
          const currentList = prev[activeFieldKey] || [];
          const updatedList = Array.isArray(currentList) && currentList?.length > 0 ? currentList.map((el) => (el.key === item.key ? updatedItem : el)) : [];
          return { ...prev, [activeFieldKey]: updatedList };
        });
      } catch (error) {
        setSuggestedData((prev) => {
          const currentList = prev[activeFieldKey] || [];
          const updatedList = currentList.map((el) => (el.key === item.key ? { ...el, isloading: false, error: true } : el));
          return { ...prev, [activeFieldKey]: updatedList };
        });
        setFormValues((prev) => {
          const currentList = prev[activeFieldKey] || [];
          const updatedList = currentList.map((el) => (el.key === item.key ? null : el));
          return { ...prev, [activeFieldKey]: updatedList };
        });
      }
    };

    loadingProductItems.forEach((item) => fetchHSCodesForItem(item));
  }, [sugestedData, activeFieldKey]);

  useEffect(() => {
    const getData = async () => {
      const res = await axios.get(getApiUrl("user_onboarding/"), {});
      const onboardingData = res.data.onboarding_data || {};
      setData(onboardingData);

      if (Object.keys(onboardingData).length > 0) {
        setIsBasicPlan(!onboardingData?.["FOUNDATIONAL_INFO"] ? false : true);
        const data = onboardingData;
        const lastVisitedPage = res.data.last_visited_page;
        const maxPageVisited = Number(Object.entries(FORMORDER).find(([, value]) => value === lastVisitedPage)?.[0]);
        setMaxStepAvailable(maxPageVisited);
        const defaultFormValues = {};

        Object.keys(data).forEach((key) => {
          const item = data[key];
          if (item?.responses) {
            const answers = item.responses;
            Object.keys(answers).forEach((field) => {
              defaultFormValues[field] = answers[field];
            });
          } else {
            let el = item;
            if (el) {
              Object.keys(el).forEach((key) => {
                const dt = item[key];
                if (dt?.responses) {
                  const answers = dt?.responses;
                  Object.keys(answers).forEach((field) => {
                    defaultFormValues[field] = answers[field];
                  });
                }
              });
            }
          }
        });

        if (BRAND.key === "source_atlas") {
          defaultFormValues.line_of_business = { textInput: "", label: LOB_ENUM.IMPORT, checked: true };
        }

        setFormValues(defaultFormValues);
        setSuggestedData(defaultFormValues);
      }
      //first comment

      const isSourceAtlasLandingOnPersona = BRAND.key === "source_atlas" && (!res.data.last_visited_page || res.data.last_visited_page === "PERSONA_INFO" || res.data.last_visited_page === "FOUNDATIONAL_INFO");
      const effectivePersonaLabel = BRAND.key === "source_atlas" ? LOB_ENUM.IMPORT : onboardingData?.PERSONA_INFO?.responses?.line_of_business?.label;

      //second comment
      if (res.data.last_visited_page === "BUSINESS_INFO_1" || res.data.last_visited_page === "BUSINESS_INFO_2" || res.data.last_visited_page === "LINE_OF_BUSINESS_INFO_1" || res.data.last_visited_page === "LINE_OF_BUSINESS_INFO_2" || res.data.last_visited_page === "USER_COMPANY_INFO") {
        setCurrentData(onboardingData[res.data.last_visited_page][effectivePersonaLabel]);
      } else if (isSourceAtlasLandingOnPersona) {
        setCurrentData(onboardingData?.USER_COMPANY_INFO?.[LOB_ENUM.IMPORT]);
      } else if (res.data.last_visited_page !== null) {
        setCurrentData(onboardingData[res.data.last_visited_page]);
      } else {
        setCurrentData(onboardingData?.["FOUNDATIONAL_INFO"] ? onboardingData["FOUNDATIONAL_INFO"] : onboardingData["PERSONA_INFO"]);
      }
      setSubBranchKey(BRAND.key === "source_atlas" ? LOB_ENUM.IMPORT : onboardingData?.PERSONA_INFO?.responses?.line_of_business?.label);
      let stepToLand = Number(Object.entries(FORMORDER).find(([, value]) => value === res.data.last_visited_page)?.[0]);
      if (isSourceAtlasLandingOnPersona) {
        setCurrentStep(3);
      } else if (res.data.last_visited_page) {
        setCurrentStep(stepToLand);
      } else {
        setCurrentStep(onboardingData?.["FOUNDATIONAL_INFO"] ? 1 : 2);
      }
      setLoader(false);
    };

    setLoader(true);
    getData();
    setDropDownData([]);
    setDropDownOpen(false);
  }, []);

  const preFetchRun = async () => {
    setLoader(true);

    try {
      const apiMaps = PRE_FETCH_API_MAPS[FORMORDER[currentStep]][subBranchKey];
      const processCategory = async (cat) => {
        let categoryData = typeof cat === "string" ? PRE_FETCH_API_MAPS[FORMORDER[currentStep]][subBranchKey][cat] : cat;
        // A global-plan account has no multicountry entitlement: intelSearchMultiCountry
        // and intelSearchMultiLatestArrivalDate both fail for it (the plan maps to no
        // indices), while the intelSearchGlobal pair works. Every other plan keeps the
        // multicountry endpoints exactly as before.
        let endpoint = categoryData?.apiEndPoint;
        if (isGlobalPlan && endpoint === "intelSearchMultiCountry") {
          endpoint = "intelSearchGlobal";
        }
        let extLevel = categoryData?.extractionDepth;
        let saveToKey = categoryData?.saveToKey;
        let url = getApiUrl(endpoint);
        let requestObj = {
          companyName: !isBasicPlan ? userData?.organizationName : (formValues?.company_name ?? ""),
          userCountry: !isBasicPlan ? userData?.country?.toLowerCase() : (formValues?.country?.[0]?.key?.toLowerCase() ?? ""),
          // searchFromDate/searchToDate are only populated for restricted plans
          // (advanced/pro/starter) - see mapSearchFromDate. On an enterprise plan they
          // are blank, and sending "" builds a range query Elasticsearch rejects
          // outright ("all shards failed"), which surfaced as a 500.
          fromDate: userCredit?.searchFromDate || earliestSearchFromDate || "",
          toDate: latestArrivalDate || userCredit?.searchToDate || "",
          dataSource: [],
          companyId: companyData.companyId ?? formValues?.companyData?.companyId,
          slug: companyData.slug ?? formValues?.companyData?.slug,
        };
        let _payload = categoryData.payload(requestObj);

        let hsCodes = formValues[STEP_FIELD_MAPS[FORMORDER[currentStep - 1]][subBranchKey]];
        const hsCodePayload = hsCodes ? hsCodes.map((item) => item.key) : null;

        if (hsCodePayload && url.toLowerCase().includes("multicountry")) {
          _payload.includeFilters = Array.isArray(_payload.includeFilters) ? [..._payload.includeFilters, "hsCode"] : ["hsCode"];
          _payload.hsCode = hsCodePayload;
        }
        if (url.toLowerCase().includes("multicountry")) {
          _payload.company_tab_trade_direction = "both";
          _payload.data_type = isMirrorSubscribed && !isDirectSubscribed ? "mirror" : "direct";
          _payload.tab = "company";
        }

        let response = await axios.post(url, _payload);
        let extData = extLevel.split(".").reduce((acc, key) => acc[key], response);
        let dataToBeSaved = extData.slice(0, 5).map((item) => ({
          key: item.key,
          description: item.key,
          issuggested: true,
          isloading: false,
          company_id: item?.hashed_id && item?.hashed_id !== "" ? item.hashed_id : item?._id && item?._id !== "" ? item._id : (item.mongo_id ?? ""),
          isBookmarked: false,
        }));

        setSuggestedData((prev) => ({
          ...prev,
          [saveToKey]: dataToBeSaved,
        }));
        setFormValues((prev) => ({
          ...prev,
          [saveToKey]: dataToBeSaved,
        }));
      };
      if (!Array.isArray(apiMaps)) {
        await Promise.all(Object.keys(apiMaps).map((cat) => processCategory(cat)));
      } else if (Array.isArray(apiMaps)) {
        await Promise.all(apiMaps.map((cat) => processCategory(cat)));
      }
    } catch (error) {
      toast.error("Something went wrong!!");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (currentStep < maxStepAvailable) {
      return;
    }
    const currentStepKey = FORMORDER[currentStep];
    if ((currentStepKey === "LINE_OF_BUSINESS_INFO_1" || (currentStepKey === "LINE_OF_BUSINESS_INFO_2" && subBranchKey === LOB_ENUM.EXPORT_AND_IMPORT)) && subBranchKey) {
      getCompanyMatch();
    } else if (currentStepKey === "BUSINESS_INFO_1" && (subBranchKey === LOB_ENUM.IMPORT || subBranchKey === LOB_ENUM.EXPORT || subBranchKey === LOB_ENUM.EXPORT_AND_IMPORT)) {
      preFetchRun();
    } else if (currentStepKey === "BUSINESS_INFO_2" && subBranchKey === LOB_ENUM.EXPORT_AND_IMPORT) {
      preFetchRun();
    }
  }, [currentStep, subBranchKey]);

  const fetchUserCompanyProfiles = async (searchCompanyName, { isInitialSearch = false } = {}) => {
    const companyName = searchCompanyName?.trim();
    if (!companyName) {
      setUserCompanyProfiles([]);
      setShowCompanySearchInput(true);
      setUserCompanyProfilesFetched(true);
      if (!isInitialSearch) {
        toast.error("Please enter company name");
      }
      if (isInitialSearch) {
        setIsInitialUserCompanySearchDone(true);
      }
      return;
    }

    try {
      setUserCompanyProfilesFetched(false);
      const response = await axios.post(getApiUrl("intelMultiCountryCompanySearch"), {
        companyName,
        probableMatchesLimit: 20,
        data_type: "direct",
      });
      const mappedProfiles = response?.data?.companies || [];
      const otherPfile = { company_id: "not_selected", key: "None of the above" };
      const profilesToShow = mappedProfiles.length > 0 ? [...mappedProfiles, otherPfile] : [];
      setUserCompanyProfiles(profilesToShow);
      setShowCompanySearchInput(isInitialSearch ? mappedProfiles.length === 0 : true);

      if (mappedProfiles.length > 0) {
        toggleUserCompanyProfileSelection(mappedProfiles[0]);
      } else {
        toggleUserCompanyProfileSelection({});
      }
    } catch (error) {
      setUserCompanyProfiles([]);
      setShowCompanySearchInput(true);
      toast.error("Unable to fetch company suggestions. Please try again.");
    } finally {
      setUserCompanyProfilesFetched(true);
      if (isInitialSearch) {
        setIsInitialUserCompanySearchDone(true);
      }
    }
  };

  useEffect(() => {
    const currentStepKey = FORMORDER[currentStep];
    const supportsUserCompanyInfo = subBranchKey === LOB_ENUM.IMPORT || subBranchKey === LOB_ENUM.EXPORT || subBranchKey === LOB_ENUM.EXPORT_AND_IMPORT;
    if (currentStepKey !== "USER_COMPANY_INFO" || !supportsUserCompanyInfo || isInitialUserCompanySearchDone) {
      return;
    }

    const initialCompanyName = userData?.organizationName || "";
    setCompanySearchText(initialCompanyName);
    fetchUserCompanyProfiles(initialCompanyName, { isInitialSearch: true });
  }, [currentStep, subBranchKey, userData?.organizationName, isInitialUserCompanySearchDone]);

  const handleUserCompanySearch = async () => {
    fetchUserCompanyProfiles(companySearchText, { isInitialSearch: false });
  };

  useEffect(() => {
    const identifier = activeFieldKey;

    const value = debouncedInputValue[identifier]?.trim() ?? "";

    const apiConfig = AUTOSUGGEST_API_MAP[FORMORDER[currentStep]]?.[subBranchKey]?.[identifier];
    if (!apiConfig || typeof apiConfig.api !== "function") {
      return;
    }

    const fetchSuggestions = async () => {
      try {
        setIsLoadingSuggestions(true);
        const url = getApiUrl(apiConfig.api(value));
        const res = await axios.get(url);

        let autoSuggestResponses = [];
        const keysToProcess = identifier === "bd_ports" ? ["ports"] : Object.keys(res.data);
        keysToProcess.forEach((key) => {
          const dataSource = identifier === "bd_ports" ? res.data?.ports : res.data[key];
          if (Array.isArray(dataSource) && dataSource.length > 0) {
            autoSuggestResponses.push(
              ...dataSource.map((item) => {
                if (typeof item === "object" && item !== null) {
                  return {
                    ...item,
                    from: key,
                    doc_count: item?.value ?? "0",
                    company_id: item?.hashed_id && item?.hashed_id !== "" ? item.hashed_id : item?._id && item?._id !== "" ? item._id : (item.mongo_id ?? ""),
                    isBookmarked: true,
                  };
                }
                return {
                  value: item,
                  label: String(item),
                  from: key,
                  doc_count: item?.value ?? "0",
                  company_id: item?.hashed_id && item?.hashed_id !== "" ? item.hashed_id : item?._id && item?._id !== "" ? item._id : (item.mongo_id ?? ""),
                  isBookmarked: true,
                };
              })
            );
          }
        });

        setDropDownData(autoSuggestResponses);
        setDropDownOpen(true);
      } catch (error) {
        setDropDownOpen(false);
      } finally {
        setTimeout(() => {
          setIsLoadingSuggestions(false);
        }, 500);
      }
    };

    fetchSuggestions();
  }, [debouncedInputValue, activeFieldKey, currentStep, subBranchKey]);

  const handleInputChange = (identifier, value, apiCalLReq, inputType = "") => {
    if (apiCalLReq) {
      setInput({ [identifier]: value });
      setActiveFieldKey(identifier);
    } else if (!apiCalLReq && identifier !== "line_of_business") {
      setInput({ ...value, [identifier]: value });
      setDropDownOpen(true);
    } else if (identifier === "line_of_business") {
      setDropDownOpen(true);
      setFormValues((prev) => ({
        ...prev,
        [identifier]: value,
      }));
    }
    if (inputType === "radio") {
      setDropDownOpen(false);
      setActiveFieldKey("");
    }
  };

  const handleKeyInput = (e) => {
    if (e.key === "Enter") {
      let params = {
        company_id: "",
        country_code: "",
        doc_count: "0",
        from: "product_description",
        isBookmarked: true,
        label: e.target.value ?? "",
        value: "0",
      };

      const onClickHandler = (val, isChecked) => {
        const value = val.label || "";
        const match = value.match(/HS:\s*(\d+)/);
        const ans = match ? match[1] : value;

        const currentSelectionCount = Array.isArray(formValues[activeFieldKey]) ? (formValues[activeFieldKey] || [])?.filter((item) => !item.issuggested).length : null;
        const limit = LIMIT_ON_SELECTION[activeFieldKey];
        if (limit && currentSelectionCount >= limit) {
          toast.error(`You can select upto only ${limit} value for this field.`);
          setInput((prev) => ({
            ...prev,
            [activeFieldKey]: "",
          }));
          return;
        }
        setCheckedOptions((prev) => ({
          ...prev,
          [activeFieldKey]: {
            ...prev[activeFieldKey],
            [val.label]: !prev[activeFieldKey]?.[val.label],
          },
        }));

        const dataToBeSaved = {
          key: ans,
          description: value,
          issuggested: false,
          country_code: val?.country_code,
          isloading: val?.from === "product_description" && (activeFieldKey === "exp_products_hscodes" || activeFieldKey === "imp_products_hscodes" || activeFieldKey === "exp_imp_products_hscodes_exp" || activeFieldKey === "exp_imp_products_hscodes_imp") ? true : false,
          from: val?.from,
          company_id: val?.company_id ?? "",
          isBookmarked: true,
        };

        setSuggestedData((prev) => {
          const existingData = prev[activeFieldKey] || [];
          const dataArray = Array.isArray(existingData) ? existingData : [];
          const nowChecked = !isChecked;
          let updatedArray = [...dataArray];
          if (nowChecked) {
            if (!updatedArray.some((el) => el.key === ans)) {
              updatedArray.push(dataToBeSaved);
            }
          } else {
            updatedArray = updatedArray.filter((el) => el.key !== ans);
          }
          const result = { ...prev, [activeFieldKey]: updatedArray };
          setFormValues((prevForm) => ({
            ...prevForm,
            [activeFieldKey]: updatedArray,
          }));
          return result;
        });

        setInput((prev) => ({
          ...prev,
          [activeFieldKey]: "",
        }));

        setDropDownData([]);
      };

      onClickHandler(params, undefined);
    }
  };

  const handleStepChange = async (action) => {
    if (action === "prev") {
      if (BRAND.key === "source_atlas" && currentStep <= 3) return;
      setLoader(true);
      setDropDownData([]);
      setValidationErrors({});

      let nextStep = currentStep - 1;
      let nextStepKey = FORMORDER[nextStep];
      trackEvent(
        `onboarding_backward_step_${currentStep}`,
        createEventData("Onboarding-Personalisation", {
          url: window.location.pathname,
        })
      );
      setUser(email);

      let attempts = 0;
      const maxAttempts = 5;

      while (attempts < maxAttempts) {
        if (!nextStepKey || !data[nextStepKey]) {
          actionPostOnboardingCompletion();
          return;
        }

        if (BRANCHES[nextStepKey]) {
          let branchKey = null;
          const selectedLineOfBusiness = formValues.line_of_business.label;
          branchKey = LINE_OF_BUSINESS_MAP[selectedLineOfBusiness];

          if (branchKey) {
            setSubBranchKey(branchKey);
            const branchedData = data[nextStepKey][branchKey];
            if (branchedData && branchedData !== null) {
              setCurrentData(branchedData);
              setCurrentStep(nextStep);
              setLoader(false);
              return;
            }
          }

          // Skip invalid step
          nextStep--;
          nextStepKey = FORMORDER[nextStep];
          attempts++;
          continue;
        } else {
          const stepData = data[nextStepKey];
          if (stepData && stepData !== null) {
            setCurrentData(stepData);
            setCurrentStep(nextStep);
            setLoader(false);
            return;
          } else {
            nextStep--;
            nextStepKey = FORMORDER[nextStep];
            attempts++;
            continue;
          }
        }
      }

      setLoader(false);
      actionPostOnboardingCompletion();
      return;
    }

    if (action === "next") {
      let hasError = false;

      if (isBasicPlan) {
        const sections = currentData?.section || [];
        const newErrors = { ...validationErrors };

        sections.forEach((item) => {
          if (!item.isMandatory) return;

          const fieldValue = formValues[item.identifier];
          let isEmpty = false;
          let isInvalid = false;
          let errorMessage = "This field is required";

          if (item.inputType === "multitext" || item.inputType === "checkbox") {
            const savedValues = sugestedData[item.identifier]?.filter((sug) => !sug.issuggested) || [];
            isEmpty = savedValues.length === 0;
          } else if (item.inputType === "radio") {
            isEmpty = !fieldValue || fieldValue.length === 0;
          } else if (item.inputType === "tel") {
            if (!fieldValue || fieldValue.toString().trim() === "") {
              isEmpty = true;
            } else {
              const cleaned = fieldValue[item.identifier].toString().replace(/[+\-\s()]/g, "");
              if (!/^\d+$/.test(cleaned) || cleaned.length < 6 || cleaned.length > 15) {
                isInvalid = true;
                errorMessage = "Please enter a valid phone number";
              }
            }
          } else if (item.inputType === "text") {
            if (fieldValue && fieldValue.toString().trim()?.length > 255) {
              isInvalid = true;
              errorMessage = "Please enter a short text input";
            }
            isEmpty = !fieldValue || fieldValue.toString().trim() === "";
          } else if (item.inputType === "number" || item.inputType === "text") {
            isEmpty = !fieldValue || fieldValue.toString().trim() === "";
          } else {
            isEmpty = !fieldValue || fieldValue === "";
          }

          if (isEmpty || isInvalid) {
            newErrors[item.identifier] = errorMessage;
            hasError = true;
          } else {
            delete newErrors[item.identifier];
          }
        });

        setValidationErrors(newErrors);

        if (hasError) {
          // Optional: scroll to first error
          const firstErrorField = sections.find((item) => newErrors[item.identifier]);
          if (firstErrorField) {
            setTimeout(() => {
              const el = document.querySelector(`[data-field="${firstErrorField.identifier}"]`);
              el?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 100);
          }
          return;
        }
      } else {
        const sections = currentData?.section || [];
        const newErrors = { ...validationErrors };

        sections.forEach((item) => {
          if (!item.isMandatory) return;

          const fieldValue = formValues[item.identifier];
          let isEmpty = false;
          let isInvalid = false;
          let errorMessage = "This field is required";

          if (item.inputType === "multitext" || item.inputType === "checkbox") {
            const savedValues = sugestedData[item.identifier]?.filter((sug) => !sug.issuggested) || [];
            isEmpty = savedValues.length === 0;
          } else if (item.inputType === "radio") {
            isEmpty = !fieldValue || fieldValue.length === 0;
          } else if (item.inputType === "tel") {
            if (!fieldValue || (typeof fieldValue === "object" && (!fieldValue.phone_number || fieldValue.phone_number.toString().trim() === ""))) {
              isEmpty = true;
            } else {
              const phoneValue = typeof fieldValue === "object" ? fieldValue.phone_number : fieldValue;
              const cleaned = phoneValue?.toString().replace(/[+\-\s()]/g, "") || "";
              if (!/^\d+$/.test(cleaned) || cleaned.length < 6 || cleaned.length > 15) {
                isInvalid = true;
                errorMessage = "Please enter a valid phone number";
              }
            }
          } else if (item.inputType === "text") {
            if (fieldValue && fieldValue.toString().trim()?.length > 255) {
              isInvalid = true;
              errorMessage = "Please enter a short text input";
            }
            isEmpty = !fieldValue || fieldValue.toString().trim() === "";
          } else if (item.inputType === "number") {
            isEmpty = !fieldValue || fieldValue.toString().trim() === "";
          } else {
            isEmpty = !fieldValue || fieldValue === "";
          }

          if (isEmpty || isInvalid) {
            newErrors[item.identifier] = errorMessage;
            hasError = true;
          } else {
            delete newErrors[item.identifier];
          }
        });

        setValidationErrors(newErrors);

        if (hasError) {
          const firstErrorField = sections.find((item) => newErrors[item.identifier]);
          if (firstErrorField) {
            setTimeout(() => {
              const el = document.querySelector(`[data-field="${firstErrorField.identifier}"]`);
              el?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 100);
          }
          return;
        }
      }

      await stepDataUpdate();

      let nextStep = currentStep + 1;
      let nextStepKey = FORMORDER[nextStep];
      setMaxStepAvailable(nextStep);

      if (currentStep >= Object.keys(FORMORDER).length) {
        updateOnboardingStatus("show_onboarding_popup", false);
        setShow(false);
        return;
      }
      let attempts = 0;
      const maxAttempts = 5;

      while (attempts < maxAttempts) {
        if (!nextStepKey || !data[nextStepKey]) {
          actionPostOnboardingCompletion();
          return;
        }

        if (BRANCHES[nextStepKey]) {
          let branchKey = null;
          const selectedLineOfBusiness = formValues.line_of_business?.label;
          branchKey = LINE_OF_BUSINESS_MAP[selectedLineOfBusiness] ?? "";

          if (branchKey) {
            setSubBranchKey(branchKey);
            const branchedData = data[nextStepKey][branchKey];

            if (branchedData && branchedData !== null) {
              setCurrentData(branchedData);
              setCurrentStep(nextStep);
              setLoader(false);
              return;
            }
          }

          // Skip invalid step
          nextStep++;
          nextStepKey = FORMORDER[nextStep];
          attempts++;
          continue;
        } else {
          const stepData = data[nextStepKey];
          if (stepData && stepData !== null) {
            setCurrentData(stepData);
            setCurrentStep(nextStep);
            setLoader(false);
            return;
          } else {
            nextStep++;
            nextStepKey = FORMORDER[nextStep];
            attempts++;
            continue;
          }
        }
      }

      setLoader(false);
      actionPostOnboardingCompletion();
    }
  };

  const stepDataUpdate = async () => {
    try {
      setLoader(true);
      const currentStepKey = FORMORDER[currentStep];
      trackEvent(
        `onboarding_forward_step_${currentStep}`,
        createEventData("Onboarding-Personalisation", {
          url: window.location.pathname,
        })
      );
      setUser(email);
      const stepData = getStepFormValues(currentStepKey);
      await axios.post(getApiUrl("user_onboarding/"), {
        page: currentStepKey,
        input_details: stepData,
      });
      if (currentStepKey === "CONFORMATION_AND_COMPLETION") {
        updateOnboardingStatus("line_of_business", formValues.line_of_business?.label);
      }
      setDropDownData([]);
    } catch (error) {
      toast.error("Something went wrong!! Please try again after sometime.");
    } finally {
      setLoader(false);
    }
  };

  const getStepFormValues = (stepKey) => {
    let fields = STEP_FIELD_MAPS[stepKey] || [];
    const stepData = {};

    if (Array.isArray(fields)) {
      fields.forEach((field) => {
        if (formValues[field] !== undefined) {
          stepData[field] = formValues[field];
        } else {
          stepData[field] = [];
        }
      });
    } else if (typeof fields === "object" && subBranchKey) {
      const branchFields = fields[subBranchKey] || [];
      branchFields.forEach((field) => {
        if (formValues[field] !== undefined) {
          stepData[field] = formValues[field];
        } else {
          stepData[field] = [];
        }
      });
    }

    return stepData;
  };

  const getCompanyMatch = async () => {
    try {
      setLoader(true);
      const data = await axios.post(getApiUrl(`intelMultiCountryCompanySearch`), {
        companyName: !isBasicPlan ? userData?.organizationName : formValues.company_name,
        probableMatchesLimit: 1,
      });

      if (data.data.totalCompaniesCount > 0) {
        const companyData = data.data.companies[0];
        if (!(companyData.nameMatchPercentage >= 90)) {
          const fieldKeys = getCurrentFieldKeys();
          setSuggestedData((prev) => {
            const updates = {};
            fieldKeys.forEach((fieldKey) => {
              updates[fieldKey] = [];
            });
            return { ...prev, ...updates };
          });
          setFormValues((prev) => {
            const updates = {};
            fieldKeys.forEach((fieldKey) => {
              updates[fieldKey] = [];
            });
            return { ...prev, ...updates };
          });
          return;
        }
        const { slug, company_id } = companyData;
        setCompanyData({ found: true, slug: slug, companyId: company_id });
        setFormValues({ ...formValues, [companyData]: { found: true, slug: slug, companyId: company_id } });

        let requestObject = {
          profile_type: "company",
          order: "desc",
          must_not: [],
          isSubsidiaryIncluded: true,
          search: null,
          sub_category: "hs_code",
          map_type: "",
          _id: company_id,
          slug: slug,
          category: FORMORDER[currentStep] === "LINE_OF_BUSINESS_INFO_1" && subBranchKey === "Export and Import" ? "exporters" : FORMORDER[currentStep] === "LINE_OF_BUSINESS_INFO_2" && subBranchKey === "Export and Import" ? "importers" : subBranchKey === "Export" ? "exporters" : "importers",
        };

        const data1 = await axios.post(getApiUrl(`profileMapData`), requestObject);

        let dataToBeSaved = data1.data.data.hs_code.slice(0, 5).map((item) => {
          return {
            key: item.key,
            description: item.short_description ?? item?.description,
            issuggested: true,
            isloading: false,
            company_id: item?.hashed_id && item?.hashed_id !== "" ? item.hashed_id : item?._id && item?._id !== "" ? item._id : (item.mongo_id ?? ""),
            isBookmarked: false,
          };
        });

        const fieldKeys = getCurrentFieldKeys();
        setSuggestedData((prev) => {
          const updates = {};
          fieldKeys.forEach((fieldKey) => {
            updates[fieldKey] = dataToBeSaved;
          });
          return { ...prev, ...updates };
        });
        setFormValues((prev) => {
          const updates = {};
          fieldKeys.forEach((fieldKey) => {
            updates[fieldKey] = dataToBeSaved;
          });
          return { ...prev, ...updates };
        });
      }

      return data;
    } catch (error) {
      return null;
    } finally {
      setLoader(false);
    }
  };

  const handleRemove = (identifier, key, idx, isSuggested = true) => {
    setSuggestedData((prev) => {
      const original = prev[identifier];

      const updated = Array.isArray(original)
        ? original.filter((item) => {
            if (item.issuggested === isSuggested && item.key === key) {
              return false;
            }
            return true;
          })
        : [];

      return { ...prev, [identifier]: updated };
    });

    setFormValues((prev) => {
      const original = prev[identifier];

      const updated = Array.isArray(original)
        ? original.filter((item) => {
            if (item.issuggested === isSuggested && item.key === key) {
              return false;
            }
            return true;
          })
        : [];

      return { ...prev, [identifier]: updated };
    });

    setCheckedOptions((prev) => {
      const currentItem = sugestedData[identifier]?.filter((x) => x.issuggested === isSuggested && x.key === key);
      if (!currentItem) return prev;

      const { description } = currentItem[0];
      const newChecked = { ...prev };
      if (newChecked[identifier]?.[description]) {
        delete newChecked[identifier][description];
        if (Object.keys(newChecked[identifier]).length === 0) {
          delete newChecked[identifier];
        }
      }
      return newChecked;
    });
  };

  const redirectToSidebar = (value) => {
    let country = "";
    let link = value.link;
    let dataSource = "";
    for (let property in globalCountryDetailsDictionary) {
      let item = property;
      if (window.location.href.includes(globalCountryDetailsDictionary[item].countryPathName) && value.name !== "Shipments") {
        country = globalCountryDetailsDictionary[item].countryUrlDashboard;
        dataSource = globalCountryDetailsDictionary[item].countryType;
        break;
      }
    }
    let query = `${link}`;
    if (country || dataSource) {
      if (country.includes("country=")) {
        query += `?${country}&type=${dataSource}`;
      } else {
        query += `?country=${country}&type=${dataSource}`;
      }
    }
    history.push(query);
  };

  const handleBookmark = (identifier, value, isBookmarked) => {
    setSuggestedData((prev) => {
      const updated = Array.isArray(prev[identifier])
        ? prev[identifier].map((item) => {
            if (item.key === value) {
              return { ...item, isBookmarked: !isBookmarked };
            }
            return item;
          })
        : [];

      return { ...prev, [identifier]: updated };
    });

    setFormValues((prev) => {
      const updated = Array.isArray(prev[identifier])
        ? prev[identifier].map((item) => {
            if (item.key === value) {
              return { ...item, isBookmarked: !isBookmarked };
            }
            return item;
          })
        : [];

      return { ...prev, [identifier]: updated };
    });
  };

  return (
    <div className="tw-w-full tw-min-h-screen">
      <div className="tw-py-6 tw-px-8 tw-flex tw-h-full">
        <div className="tw-bg-gray-50 tw-w-1/3 tw-flex tw-flex-col tw-justify-between tw-h-[90vh] tw-px-4 tw-py-6 tw-rounded-lg tw-shadow-lg">
          <div className="tw-flex tw-items-center tw-gap-3">
            <img src={BRAND.logo} alt="dashboard logo" />
            <div className="tw-text-base tw-font-semibold tw-text-gray-900">{BRAND.name}</div>
          </div>
          <div className="tw-my-4">
            {" "}
            <img className="tw-w-full tw-rounded-lg" src={`https://res.cloudinary.com/drzta9shk/image/upload/v1763359760/intel/misc/Intel_onboarding_image_1_e2y1ei.webp`} alt="Product Screenshot" />{" "}
          </div>
          <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-base tw-font-semibold">Email Support</span>
              <span className="tw-text-sm tw-font-normal">We will get back to you within 24 working hours</span>
            </div>
            <div>
              <a className="tw-text-base !tw-underline tw-font-semibold tw-text-primary-purple-700" href="mailto:support@trademo.com">
                support@trademo.com
              </a>
            </div>
          </div>
        </div>
        <div className={`tw-flex tw-w-full tw-p-6 ${loader ? "tw-items-center tw-justify-center" : ""}`}>
          {loader ? (
            <div className="tw-h-[calc(100vh-240px)] tw-flex tw-items-center tw-justify-center">
              <img src={LoaderImg} alt="loading" />
            </div>
          ) : (
            <>
              <div className="tw-flex tw-flex-col tw-py-11 tw-gap-11 tw-px-20 tw-w-full">
                {currentData.header && (
                  <div className="tw-flex tw-flex-col tw-gap-3">
                    {/* <span>
                      <div className="material-symbols-outlined tw-w-[58.7px] tw-h-12 tw-bg-[linear-gradient(to bottom, #ffbdfe 0%, #99f 50%, #613bc2)]"></div>
                    </span> */}
                    <span className="tw-text-xl tw-font-semibold">{currentData.header.title}</span>
                    <span className="tw-text-sm tw-font-normal">{currentData.header.subheader}</span>
                  </div>
                )}

                <div className="tw-flex tw-flex-col tw-gap-6">
                  {currentData.section &&
                    currentData.section.map((item) => {
                      if (item.identifier === "user_company_profile") {
                        return <UserCompanyProfileCardView key={item.identifier} tableData={userCompanyProfiles} selectedCompany={formValues.user_company_profile} onToggleProfile={toggleUserCompanyProfileSelection} userCompanyProfilesFetched={userCompanyProfilesFetched} showCompanySearchInput={isInitialUserCompanySearchDone && showCompanySearchInput} companySearchText={companySearchText} onCompanySearchTextChange={setCompanySearchText} onSearchCompanyProfiles={handleUserCompanySearch} />;
                      }
                      return (
                        <div key={item.identifier} className="tw-flex tw-flex-col tw-gap-2" data-field={item.identifier}>
                          <span className="tw-text-sm tw-font-semibold">
                            {item.title} {item.isMandatory ? "*" : ""}
                          </span>
                          {item.label && <span className="tw-text-sm tw-font-normal">{item.label}</span>}

                          <div className="tw-relative tw-flex tw-flex-col tw-gap-2">
                            {item.inputType === "tel" && (
                              <>
                                <div className="tw-relative tw-w-full tw-flex tw-gap-1">
                                  <div
                                    className="tw-relative tw-min-w-[120px] tw-border tw-border-gray-300 tw-rounded-lg tw-bg-gray-50 tw-flex tw-items-center tw-px-2 tw-cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveFieldKey(item.identifier);
                                      setDropDownOpen((prev) => !prev);
                                    }}>
                                    {formValues[item.identifier]?.countryCode ? (
                                      <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium">
                                        <ReactCountryFlag countryCode={formValues[item.identifier]?.countryCode} svg className="tw-w-5 tw-h-4" />
                                        {formValues[item.identifier]?.telCode || ""}
                                      </span>
                                    ) : (
                                      <span className="tw-text-gray-500 tw-flex tw-items-center tw-gap-1.5 tw-text-sm">
                                        <ReactCountryFlag countryCode={item.values[0]?.country_code} svg className="tw-w-5 tw-h-4" />
                                        {item.values[0]?.tel_code || ""}
                                      </span>
                                    )}

                                    <span className="tw-ml-auto tw-text-gray-400">
                                      <span className="material-symbols-outlined tw-text-base">expand_more</span>
                                    </span>

                                    {dropDownOpen && activeFieldKey === item.identifier && (
                                      <div className="tw-absolute tw-top-full tw-w-fit tw-left-0 tw-mt-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-z-20 tw-max-h-60 tw-overflow-y-auto">
                                        {item.values?.map((country, idx) => {
                                          const phoneData = item.values[idx];
                                          const dial = phoneData?.tel_code || "";
                                          const label = phoneData?.label || "";
                                          return (
                                            <div
                                              key={phoneData.country_code}
                                              className="tw-px-3 tw-py-2 hover:tw-bg-gray-50 tw-cursor-pointer tw-flex tw-items-center tw-gap-3 tw-text-sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setFormValues((prev) => ({
                                                  ...prev,
                                                  [item.identifier]: {
                                                    countryCode: phoneData.country_code,
                                                    country: phoneData.label,
                                                    phone_number: "",
                                                    telCode: phoneData.tel_code,
                                                  },
                                                }));
                                                setDropDownOpen(false);
                                                setActiveFieldKey("");
                                              }}>
                                              <ReactCountryFlag countryCode={country.country_code} svg className="tw-w-5 tw-h-4" />
                                              <span className="tw-flex-1">{label}</span>
                                              <span className="tw-text-gray-600 tw-font-medium">{dial}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>

                                  <div
                                    className="tw-flex-1 tw-min-h-[38px] tw-py-1.5 tw-px-2 tw-border tw-border-gray-300 tw-rounded-lg tw-bg-gray-50 tw-flex tw-items-center"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDropDownOpen(false);
                                      setActiveFieldKey(item.identifier);
                                    }}>
                                    {/* Input number 1 for phone number only */}
                                    <input
                                      type="tel"
                                      required={item.isMandatory}
                                      placeholder={item.placeholder}
                                      value={formValues[item.identifier]?.phone_number || ""}
                                      onChange={(e) => {
                                        setFormValues((prev) => ({
                                          ...prev,
                                          [item.identifier]: {
                                            ...prev[item.identifier],
                                            phone_number: e.target.value,
                                          },
                                        }));
                                      }}
                                      onFocus={() => {
                                        setActiveFieldKey(item.identifier);
                                      }}
                                      onClick={() => setDropDownOpen(true)}
                                      className="tw-flex-1 tw-border-0 tw-outline-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium"
                                    />
                                  </div>
                                </div>
                                <span className="tw-flex tw-text-sm tw-font-medium tw-text-red-500">{validationErrors[item.identifier] ? validationErrors[item.identifier] : ""}</span>
                              </>
                            )}
                            {item.inputType !== "radio" && item.inputType !== "checkbox" && item.inputType !== "button" && item.inputType !== "tel" && (
                              <div
                                ref={(el) => {
                                  if (item.identifier === activeFieldKey && el) {
                                    dropDownRefs.current[item.identifier] = el;
                                  }
                                }}>
                                <div className="tw-relative tw-w-full tw-min-h-[38px] tw-py-1.5 tw-px-2 tw-border tw-border-gray-300 tw-rounded-lg tw-bg-gray-50 tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center">
                                  {item.inputType === "multitext" &&
                                    sugestedData[item.identifier] &&
                                    Array.isArray(sugestedData[item.identifier]) &&
                                    (() => {
                                      const filteredItems = sugestedData[item.identifier].filter((sugItem) => !sugItem.issuggested);
                                      const isExpanded = showAllTags[item.identifier];
                                      const visibleItems = filteredItems.slice(0, SLICE_MAPS[item.identifier]);
                                      const remainingCount = filteredItems.length - SLICE_MAPS[item.identifier];

                                      return (
                                        <>
                                          <div className="">
                                            <div className="tw-flex tw-gap-1">
                                              {visibleItems.map((sugItem) => {
                                                const originalIdx = sugestedData[item.identifier].findIndex((original) => original.key === sugItem.key && !original.issuggested);

                                                return (
                                                  <>
                                                    <span key={sugItem.key} data-for={"company-name"} data-tip={sugItem.key.length > 20 ? getTitleCase(sugItem.key) : item.identifier?.includes("hscodes") && sugItem.description.length > 20 ? getTitleCase(sugItem.description) : ""} className="tw-inline-flex tw-items-center tw-gap-1 tw-border tw-border-[#7143f1] tw-rounded-xl tw-px-2 tw-py-0.5 tw-bg-white tw-text-sm tw-font-medium">
                                                      {sugItem?.country_code ? <ReactCountryFlag countryCode={sugItem?.country_code} /> : <></>}
                                                      <span className="tw-text-sm tw-text-gray-900 tw-font-normal">{item.identifier?.includes("hscodes") ? (sugItem.description.includes(sugItem.key) ? sugItem.key : sugItem.description) : sugItem.key.length > 20 ? (item.identifier === "business_size" ? sugItem.key.slice(0, 20) + "..." : getTitleCase(sugItem.key).slice(0, 20) + "...") : getTitleCase(sugItem.key)}</span>
                                                      <button
                                                        type="button"
                                                        onClick={() => {
                                                          handleRemove(item.identifier, sugItem.key, originalIdx, false);
                                                        }}
                                                        className="tw-text-gray-900 tw-bg-white tw-font-bold">
                                                        ×
                                                      </button>
                                                    </span>
                                                    {(sugItem.key.length > 20 || (sugItem.description.length > 20 && item.identifier?.includes("hscodes"))) && <ReactTooltip id={"company-name"} className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="top" />}
                                                  </>
                                                );
                                              })}
                                              {remainingCount > 0 && (
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    setShowAllTags((prev) => ({
                                                      ...prev,
                                                      [item.identifier]: !prev[item.identifier],
                                                    }))
                                                  }
                                                  className="tw-inline-flex tw-items-center tw-gap-1 tw-border tw-border-[#7143f1] tw-rounded-xl tw-px-2 tw-py-0.5 tw-bg-[#7143f1] tw-text-white tw-text-sm tw-font-medium hover:tw-bg-[#5f39d1] tw-transition-colors">
                                                  {isExpanded ? "−" : `+${remainingCount}`}
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        </>
                                      );
                                    })()}
                                  {item.inputType === "multitext" && showAllTags[item.identifier] && sugestedData[item.identifier].filter((sugItem) => !sugItem.issuggested).slice(SLICE_MAPS[item.identifier], sugestedData[item.identifier].filter((sugItem) => !sugItem.issuggested).length).length > 0 && (
                                    <div id="extra-items" className="tw-absolute tw-z-10 tw-flex tw-gap-1 tw-w-full tw-top-full tw-left-0 tw-bg-white tw-border-gray-200 tw-shadow-lg tw-border tw-rounded-xl tw-p-4">
                                      {sugestedData[item.identifier]
                                        .filter((sugItem) => !sugItem.issuggested)
                                        .slice(SLICE_MAPS[item.identifier], sugestedData[item.identifier].filter((sugItem) => !sugItem.issuggested).length)
                                        .map((sugItem) => {
                                          const originalIdx = sugestedData[item.identifier].findIndex((original) => original.key === sugItem.key && !original.issuggested);
                                          return (
                                            <>
                                              <span data-tip={getTitleCase(sugItem.key)} data-for={"company-name"} key={sugItem.key} className="tw-inline-flex tw-items-center tw-gap-1 tw-border tw-border-[#7143f1] tw-rounded-xl tw-px-2 tw-py-0.5 tw-bg-white tw-text-sm tw-font-medium">
                                                {sugItem?.country_code ? <ReactCountryFlag countryCode={sugItem?.country_code} /> : <></>}
                                                <span className="tw-text-sm tw-text-gray-900 tw-font-normal">{sugItem.key.length > 20 ? (item.identifier === "business_size" ? sugItem.key.slice(0, 20) + "..." : getTitleCase(sugItem.key).slice(0, 20) + "...") : getTitleCase(sugItem.key)}</span>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    handleRemove(item.identifier, sugItem.key, originalIdx, false);
                                                  }}
                                                  className="tw-text-gray-900 tw-bg-white tw-font-bold">
                                                  ×
                                                </button>
                                              </span>
                                              {sugItem.key.length > 20 && <ReactTooltip id={"company-name"} className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="top" />}
                                            </>
                                          );
                                        })}
                                    </div>
                                  )}
                                  {/* input number 2 for multitext */}
                                  <input
                                    type="text"
                                    required={item.isMandatory}
                                    placeholder={formValues && Array.isArray(formValues[item.identifier]) && formValues[item.identifier]?.some((el) => !el.issuggested) ? "" : item.placeholder}
                                    onFocus={() => {
                                      setActiveFieldKey(item.identifier);
                                    }}
                                    onClick={() => setDropDownOpen(true)}
                                    className="tw-flex-1 tw-min-w-[120px] tw-border-0 tw-outline-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium"
                                    value={item.inputType === "multitext" ? input[item.identifier] : formValues[item.identifier] || ""}
                                    onChange={(e) => {
                                      handleInputChange(item.identifier, e.target.value, item.inputType === "multitext", item.inputType);
                                      setFormValues((prev) => ({
                                        ...prev,
                                        [item.identifier]: e.target.value,
                                      }));
                                    }}
                                    onKeyDown={handleKeyInput}
                                  />
                                </div>
                                <span className="tw-flex tw-text-sm tw-font-medium tw-text-red-500">{validationErrors[item.identifier] ? validationErrors[item.identifier] : ""}</span>
                                {dropDownOpen && activeFieldKey === item.identifier && item.inputType !== "tel" && <DropDownOnSearch isLoadingSuggestions={isLoadingSuggestions} data={item.values || dropDownData} currentStep={currentStep} setSuggestedData={setSuggestedData} setInput={setInput} setDropDownOpen={setDropDownOpen} currentFieldKey={item.identifier} setFormValues={setFormValues} dropDownInputType={item.inputType} searchInput={input[item.identifier] || ""} setCheckedOptions={setCheckedOptions} checkedOptions={checkedOptions} sugestedData={sugestedData} formValues={formValues} setDropDownData={setDropDownData} />}
                              </div>
                            )}
                            {item.inputType === "radio" && item.values && Array.isArray(item.values) && (
                              <>
                                <div className={`tw-flex tw-gap-2 ${item.identifier === "line_of_business" ? "tw-flex-col" : "tw-flex-wrap"}`}>
                                  {item.values.map((el) => (
                                    <label key={el.value} className="tw-flex tw-items-center tw-gap-2 tw-py-2 tw-px-3 tw-border tw-rounded-lg tw-shadow-autoSuggestPill tw-cursor-pointer hover:tw-bg-gray-50">
                                      <input
                                        type="radio"
                                        className="tw-accent-primary-purple-600 !tw-mx-0"
                                        name={item.identifier}
                                        value={el.value}
                                        checked={formValues[item.identifier]?.checked && formValues[item.identifier]?.label === el.value}
                                        onChange={(e) => {
                                          handleInputChange(item.identifier, e.target.value, undefined, item.inputType);
                                          setFormValues((prev) => ({
                                            ...prev,
                                            [item.identifier]: { textInput: "", label: el.value, checked: true },
                                          }));
                                        }}
                                      />
                                      <span className="tw-text-sm tw-font-normal">
                                        {LOB_LABEL_MAP[el.value] ? (
                                          <>
                                            <span className="tw-font-semibold">{LOB_LABEL_MAP[el.value].split("::")[0]}</span>: {LOB_LABEL_MAP[el.value].split("::")[1]}
                                          </>
                                        ) : (
                                          el.value
                                        )}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                                <span className="tw-flex tw-text-sm tw-font-medium tw-text-red-500">{validationErrors[item.identifier] ? validationErrors[item.identifier] : ""}</span>
                              </>
                            )}

                            {formValues[item.identifier]?.label === "Other" && formValues[item.identifier]?.checked && (
                              <input
                                type="text"
                                required={item.isMandatory}
                                placeholder={item.placeholder}
                                className="tw-w-full tw-py-1.5 tw-border tw-border-gray-300 tw-rounded-lg tw-pl-2 tw-bg-gray-50"
                                value={formValues[item.identifier].textInput || ""}
                                onChange={(e) => {
                                  setFormValues((prev) => ({
                                    ...prev,
                                    [item.identifier]: { textInput: e.target.value, label: "Other", checked: true },
                                  }));
                                }}
                              />
                            )}
                            {item.inputType === "checkbox" && item.inputType !== "button" && (
                              <>
                                <div
                                  ref={(el) => {
                                    if (item.identifier === activeFieldKey && el) {
                                      dropDownRefs.current[item.identifier] = el;
                                    }
                                  }}>
                                  <div className="tw-relative tw-w-full tw-min-h-[38px] tw-py-1.5 tw-px-2 tw-border tw-border-gray-300 tw-rounded-lg tw-bg-gray-50 tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center">
                                    {(item.inputType === "checkbox" || item.inputType === "number") &&
                                      sugestedData[item.identifier] &&
                                      Array.isArray(sugestedData[item.identifier]) &&
                                      (() => {
                                        const filteredItems = sugestedData[item.identifier].filter((sugItem) => !sugItem.issuggested);
                                        const isExpanded = showAllTags[item.identifier];
                                        const visibleItems = filteredItems.slice(0, SLICE_MAPS[item.identifier]);
                                        const remainingCount = filteredItems.length - SLICE_MAPS[item.identifier];

                                        return (
                                          <>
                                            <div className="">
                                              <div className="tw-flex tw-gap-1">
                                                {visibleItems.map((sugItem) => {
                                                  const originalIdx = sugestedData[item.identifier].findIndex((original) => original.key === sugItem.key && !original.issuggested);

                                                  return (
                                                    <>
                                                      <span data-tip={getTitleCase(sugItem.key)} data-for={"company-name"} key={sugItem.key} className="tw-inline-flex tw-items-center tw-gap-1 tw-border tw-border-[#7143f1] tw-rounded-xl tw-px-2 tw-py-0.5 tw-bg-white tw-text-sm tw-font-medium">
                                                        {sugItem?.country_code ? <ReactCountryFlag countryCode={sugItem?.country_code} /> : <></>}
                                                        <span className="tw-text-sm tw-text-gray-900 tw-font-normal">{sugItem.key.length > 20 ? (item.identifier === "business_size" ? sugItem.key.slice(0, 20) + "..." : getTitleCase(sugItem.key).slice(0, 20) + "...") : getTitleCase(sugItem.key)}</span>
                                                        <button
                                                          type="button"
                                                          onClick={() => {
                                                            handleRemove(item.identifier, sugItem.key, originalIdx, false);
                                                          }}
                                                          className="tw-text-gray-900 tw-bg-white tw-font-bold">
                                                          ×
                                                        </button>
                                                      </span>
                                                      {sugItem.key.length > 20 && <ReactTooltip id={"company-name"} className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="top" />}
                                                    </>
                                                  );
                                                })}
                                                {remainingCount > 0 && (
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      setShowAllTags((prev) => ({
                                                        ...prev,
                                                        [item.identifier]: !prev[item.identifier],
                                                      }))
                                                    }
                                                    className="tw-inline-flex tw-items-center tw-gap-1 tw-border tw-border-[#7143f1] tw-rounded-xl tw-px-2 tw-py-0.5 tw-bg-[#7143f1] tw-text-white tw-text-sm tw-font-medium hover:tw-bg-[#5f39d1] tw-transition-colors">
                                                    {isExpanded ? "−" : `+${remainingCount}`}
                                                  </button>
                                                )}
                                              </div>
                                            </div>
                                          </>
                                        );
                                      })()}
                                    {showAllTags[item.identifier] && sugestedData[item.identifier].filter((sugItem) => !sugItem.issuggested).slice(SLICE_MAPS[item.identifier], sugestedData[item.identifier].filter((sugItem) => !sugItem.issuggested).length).length > 0 && (
                                      <div id="extra-items" className="tw-absolute tw-z-10 tw-w-full tw-flex tw-gap-1 tw-top-full tw-left-0 tw-bg-white tw-border-gray-200 tw-shadow-lg tw-border tw-rounded-xl tw-p-4">
                                        {sugestedData[item.identifier]
                                          .filter((sugItem) => !sugItem.issuggested)
                                          .slice(SLICE_MAPS[item.identifier], sugestedData[item.identifier].filter((sugItem) => !sugItem.issuggested).length)
                                          .map((sugItem) => {
                                            const originalIdx = sugestedData[item.identifier].findIndex((original) => original.key === sugItem.key && !original.issuggested);
                                            return (
                                              <>
                                                <span data-tip={getTitleCase(sugItem.key)} data-for={"company-name"} key={sugItem.key} className="tw-inline-flex tw-items-center tw-gap-1 tw-border tw-border-[#7143f1] tw-rounded-xl tw-px-2 tw-py-0.5 tw-bg-white tw-text-sm tw-font-medium">
                                                  {sugItem?.country_code ? <ReactCountryFlag countryCode={sugItem?.country_code} /> : <></>}
                                                  <span className="tw-text-sm tw-text-gray-900 tw-font-normal">{sugItem.key.length > 20 ? (item.identifier === "business_size" ? sugItem.key.slice(0, 20) : getTitleCase(sugItem.key).slice(0, 20) + "...") : getTitleCase(sugItem.key)}</span>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      handleRemove(item.identifier, sugItem.key, originalIdx, false);
                                                    }}
                                                    className="tw-text-gray-900 tw-bg-white tw-font-bold">
                                                    ×
                                                  </button>
                                                </span>
                                                {sugItem.key.length > 20 && <ReactTooltip id={"company-name"} className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="top" />}
                                              </>
                                            );
                                          })}
                                      </div>
                                    )}
                                    <input
                                      type="text"
                                      required={item.isMandatory}
                                      placeholder={formValues && Array.isArray(formValues[item.identifier]) && formValues[item.identifier]?.some((el) => !el.issuggested) ? "" : item.placeholder}
                                      onFocus={() => {
                                        setActiveFieldKey(item.identifier);
                                      }}
                                      className="tw-flex-1 tw-min-w-[120px] tw-border-0 tw-outline-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium"
                                      value={item.inputType === "checkbox" ? input[item.identifier] : formValues[item.identifier] || ""}
                                      onChange={(e) => handleInputChange(item.identifier, e.target.value, !item?.values && item.inputType === "checkbox")}
                                      onClick={() => setDropDownOpen(true)}
                                    />
                                  </div>
                                  <span className="tw-flex tw-text-sm tw-font-medium tw-text-red-500">{validationErrors[item.identifier] ? validationErrors[item.identifier] : ""}</span>
                                  {dropDownOpen && activeFieldKey === item.identifier && <DropDownOnSearch isLoadingSuggestions={isLoadingSuggestions} data={item.values || dropDownData} currentStep={currentStep} setSuggestedData={setSuggestedData} setInput={setInput} setDropDownOpen={setDropDownOpen} currentFieldKey={item.identifier} setFormValues={setFormValues} dropDownInputType={item.inputType} searchInput={input[item.identifier] || ""} setCheckedOptions={setCheckedOptions} checkedOptions={checkedOptions} sugestedData={sugestedData} formValues={formValues} setDropDownData={setDropDownData} />}
                                </div>
                                {sugestedData[item.identifier] && Array.isArray(sugestedData[item.identifier]) && sugestedData[item.identifier]?.length > 0 && sugestedData[item.identifier]?.filter((el) => el.issuggested)?.length > 0 && item?.showSuggestionBox ? (
                                  <div key={`trade-${item.identifier}`} className="tw-flex tw-flex-col tw-gap-2 tw-border tw-border-[#f6dd6e] tw-bg-[#fffef9] tw-rounded-lg tw-p-4">
                                    <div className="tw-flex tw-items-center tw-gap-1">
                                      <span className="material-symbols-outlined">emoji_objects</span>
                                      <span className="tw-text-sm tw-font-medium">{item?.suggestionHeader ?? ""}</span>
                                    </div>
                                    {sugestedData[item.identifier]
                                      .filter((el) => el.issuggested)
                                      .slice(0, 10)
                                      .map((et) => (
                                        <div key={et.key} className="tw-flex tw-gap-2 tw-py-2 tw-px-4 tw-border tw-rounded-lg tw-shadow-sm tw-bg-white tw-justify-between">
                                          <span className="tw-text-sm tw-font-medium">{getTitleCase(et.description.slice(0, 50))}</span>
                                          <div className="tw-flex tw-gap-2 tw-items-center">
                                            <button data-for="actions" data-tip="Bookmark" onClick={() => handleBookmark(item.identifier, et.key, false)} className="tw-cursor-pointer tw-rounded-lg tw-border tw-bg-primary-purple-100 tw-w-fit tw-h-6 tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-border-none" aria-label="Favorite">
                                              {!et.isBookmarked ? (
                                                <span className="tw-flex tw-justify-start tw-items-center tw-py-0.5 tw-px-2 tw-border-none tw-rounded-lg tw-bg-primary-purple-50 tw-gap-0.5">
                                                  {item.identifier.includes("hscodes") ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#5a1ce2">
                                                      <path d="M816-672v456q0 29.7-21.15 50.85Q773.7-144 744-144H216q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h456l144 144Zm-72 30L642-744H216v528h528v-426ZM480-252q45 0 76.5-31.5T588-360q0-45-31.5-76.5T480-468q-45 0-76.5 31.5T372-360q0 45 31.5 76.5T480-252ZM264-552h336v-144H264v144Zm-48-77v413-528 115Z" />
                                                    </svg>
                                                  ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="#5a1ce2">
                                                      <path d="m352-293 128-76 129 76-34-144 111-95-147-13-59-137-59 137-147 13 112 95-34 144Zm-79 107.46 55.31-231.77-183.46-155.92 240.61-20.92L480-812.84l94.54 219.69 240.61 19.92-183.46 155.92L687-185.54 480-308.46 273-185.54ZM480-477Z" />
                                                    </svg>
                                                  )}
                                                  <span className="tw-text-sm tw-text-primary-purple-600">{item.identifier.includes("hscodes") ? "Save" : "Bookmark"}</span>
                                                </span>
                                              ) : (
                                                <span className="tw-text-sm tw-text-primary-purple-600 tw-py-0.5 tw-px-2 tw-flex tw-items-center tw-border tw-rounded-lg tw-gap-0.5">
                                                  {item.identifier.includes("hscodes") ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#5a1ce2">
                                                      <path d="M816-672v456q0 29.7-21.15 50.85Q773.7-144 744-144H216q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h456l144 144ZM480-252q45 0 76.5-31.5T588-360q0-45-31.5-76.5T480-468q-45 0-76.5 31.5T372-360q0 45 31.5 76.5T480-252ZM264-552h336v-144H264v144Z" />
                                                    </svg>
                                                  ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="24px" fill="#5a1ce2">
                                                      <path d="m233-120 65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Z" />
                                                    </svg>
                                                  )}
                                                  <span className="tw-text-sm tw-text-primary-purple-600">{item.identifier.includes("hscodes") ? "Saved" : "Bookmarked"}</span>
                                                </span>
                                              )}
                                            </button>
                                            {et.isBookmarked && (
                                              <span
                                                className="tw-text-sm tw-text-gray-500 tw-py-0.5 tw-px-2 tw-flex tw-items-center tw-border tw-rounded-lg tw-cursor-pointer"
                                                onClick={() => {
                                                  handleBookmark(item.identifier, et.key, et.isBookmarked);
                                                }}>
                                                <span className="material-symbols-outlined">delete</span>
                                                <span>Remove</span>
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                ) : (
                                  <></>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}

                  {(FORMORDER[currentStep] === "LINE_OF_BUSINESS_INFO_1" || (FORMORDER[currentStep] === "LINE_OF_BUSINESS_INFO_2" && subBranchKey === "Export and Import")) &&
                    getCurrentFieldKeys().map(
                      (fieldKey) =>
                        sugestedData[fieldKey] &&
                        Array.isArray(sugestedData[fieldKey]) &&
                        sugestedData[fieldKey].some((item) => !item.issuggested) && (
                          <div key={`ai-${fieldKey}`} className="tw-flex tw-flex-col tw-gap-2 tw-border tw-border-[#c6f66e] tw-bg-[#fffef9] tw-rounded-lg tw-p-4">
                            <div className="tw-flex tw-items-center tw-gap-1">
                              <span className="material-symbols-outlined">
                                <img src={sparkleImg} className="tw-w-6 tw-h-6 " />
                              </span>
                              <span className="tw-text-sm tw-font-medium">Our AI classifier has mapped following HS Codes with products to give you better results</span>
                            </div>
                            {sugestedData[fieldKey]
                              .filter((item) => !item.issuggested)
                              .slice(0, 10)
                              .map((item, idx) =>
                                item.isloading ? (
                                  <LoadingSkeleton key={idx} lines={1} lineHeight="36px" width="100%" shape="rectangle" />
                                ) : (
                                  <div key={item.key} className="tw-flex tw-gap-2 tw-py-2 tw-px-4 tw-border tw-rounded-lg tw-shadow-sm tw-justify-between tw-bg-white">
                                    <span className="tw-flex tw-items-center tw-gap-2">
                                      <span className="tw-text-sm tw-font-medium">{item.description.slice(0, 50)}</span>
                                      <span className="tw-bg-[#d9ffe0] tw-rounded-lg tw-text-sm tw-font-normal tw-py-0 tw-px-2">{`HS Code: ${item.key}`}</span>
                                    </span>
                                    <div className="tw-flex tw-gap-2 tw-items-center">
                                      <button data-for="actions" data-tip="Bookmark" onClick={() => handleBookmark(fieldKey, item.key, false)} className="tw-cursor-pointer tw-rounded-lg tw-border tw-bg-primary-purple-100 tw-w-fit tw-h-6 tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-border-none" aria-label="Favorite">
                                        {!item.isBookmarked ? (
                                          <span className="tw-flex tw-justify-start tw-items-center tw-py-0.5 tw-px-2 tw-border-none tw-rounded-lg tw-bg-primary-purple-50 tw-gap-0.5">
                                            {fieldKey.includes("hscodes") ? (
                                              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#5a1ce2">
                                                <path d="M816-672v456q0 29.7-21.15 50.85Q773.7-144 744-144H216q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h456l144 144Zm-72 30L642-744H216v528h528v-426ZM480-252q45 0 76.5-31.5T588-360q0-45-31.5-76.5T480-468q-45 0-76.5 31.5T372-360q0 45 31.5 76.5T480-252ZM264-552h336v-144H264v144Zm-48-77v413-528 115Z" />
                                              </svg>
                                            ) : (
                                              <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="#5a1ce2">
                                                <path d="m352-293 128-76 129 76-34-144 111-95-147-13-59-137-59 137-147 13 112 95-34 144Zm-79 107.46 55.31-231.77-183.46-155.92 240.61-20.92L480-812.84l94.54 219.69 240.61 19.92-183.46 155.92L687-185.54 480-308.46 273-185.54ZM480-477Z" />
                                              </svg>
                                            )}
                                            <span className="tw-text-sm tw-text-primary-purple-600">{fieldKey.includes("hscodes") ? "Save" : "Bookmark"}</span>
                                          </span>
                                        ) : (
                                          <span className="tw-text-sm tw-text-primary-purple-600 tw-py-0.5 tw-px-2 tw-flex tw-items-center tw-border tw-rounded-lg tw-gap-0.5">
                                            {fieldKey.includes("hscodes") ? (
                                              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#5a1ce2">
                                                <path d="M816-672v456q0 29.7-21.15 50.85Q773.7-144 744-144H216q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h456l144 144ZM480-252q45 0 76.5-31.5T588-360q0-45-31.5-76.5T480-468q-45 0-76.5 31.5T372-360q0 45 31.5 76.5T480-252ZM264-552h336v-144H264v144Z" />
                                              </svg>
                                            ) : (
                                              <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="24px" fill="#5a1ce2">
                                                <path d="m233-120 65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Z" />
                                              </svg>
                                            )}
                                            <span className="tw-text-sm tw-text-primary-purple-600">{fieldKey.includes("hscodes") ? "Saved" : "Bookmarked"}</span>
                                          </span>
                                        )}
                                      </button>
                                      {item.isBookmarked && (
                                        <span
                                          className="tw-text-sm tw-text-gray-500 tw-py-0.5 tw-px-2 tw-flex tw-items-center tw-border tw-rounded-lg tw-cursor-pointer"
                                          onClick={() => {
                                            handleBookmark(fieldKey, item.key, item.isBookmarked);
                                          }}>
                                          <span className="material-symbols-outlined">delete</span>
                                          <span>Remove</span>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )
                              )}
                          </div>
                        )
                    )}

                  {(FORMORDER[currentStep] === "LINE_OF_BUSINESS_INFO_1" || (subBranchKey === "Export and Import" && FORMORDER[currentStep] === "LINE_OF_BUSINESS_INFO_2")) &&
                    getCurrentFieldKeys().map(
                      (fieldKey) =>
                        sugestedData[fieldKey] &&
                        Array.isArray(sugestedData[fieldKey]) &&
                        sugestedData[fieldKey].some((el) => el.issuggested) && (
                          <div key={`trade-${fieldKey}`} className="tw-flex tw-flex-col tw-gap-2 tw-border tw-border-[#f6dd6e] tw-bg-[#fffef9] tw-rounded-lg tw-p-4">
                            <div className="tw-flex tw-items-center tw-gap-1">
                              <span className="material-symbols-outlined">emoji_objects</span>
                              <span className="tw-text-sm tw-font-medium">We have found the following products that your company deals in</span>
                            </div>
                            {sugestedData[fieldKey]
                              .filter((item) => item.issuggested)
                              .slice(0, 10)
                              .map((item) => (
                                <div key={item.key} className="tw-flex tw-gap-2 tw-py-2 tw-px-4 tw-border tw-rounded-lg tw-shadow-sm tw-bg-white tw-justify-between">
                                  <span className="tw-flex tw-items-center tw-gap-2">
                                    <span className="tw-text-sm tw-font-medium">{item.description.slice(0, 50)}</span>
                                    <span className="tw-bg-[#fdf7c9] tw-rounded-lg tw-text-sm tw-font-normal tw-py-0 tw-px-2">{`HS Code: ${item.key}`}</span>
                                  </span>
                                  <div className="tw-flex tw-gap-2 tw-items-center">
                                    <button data-for="actions" data-tip="Bookmark" onClick={() => handleBookmark(fieldKey, item.key, false)} className="tw-cursor-pointer tw-rounded-lg tw-border tw-bg-primary-purple-100 tw-w-fit tw-h-6 tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-border-none" aria-label="Favorite">
                                      {!item.isBookmarked ? (
                                        <span className="tw-flex tw-justify-start tw-items-center tw-py-0.5 tw-px-2 tw-border-none tw-rounded-lg tw-bg-primary-purple-50 tw-gap-0.5">
                                          {fieldKey.includes("hscodes") ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#5a1ce2">
                                              <path d="M816-672v456q0 29.7-21.15 50.85Q773.7-144 744-144H216q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h456l144 144Zm-72 30L642-744H216v528h528v-426ZM480-252q45 0 76.5-31.5T588-360q0-45-31.5-76.5T480-468q-45 0-76.5 31.5T372-360q0 45 31.5 76.5T480-252ZM264-552h336v-144H264v144Zm-48-77v413-528 115Z" />
                                            </svg>
                                          ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="#5a1ce2">
                                              <path d="m352-293 128-76 129 76-34-144 111-95-147-13-59-137-59 137-147 13 112 95-34 144Zm-79 107.46 55.31-231.77-183.46-155.92 240.61-20.92L480-812.84l94.54 219.69 240.61 19.92-183.46 155.92L687-185.54 480-308.46 273-185.54ZM480-477Z" />
                                            </svg>
                                          )}
                                          <span className="tw-text-sm tw-text-primary-purple-600">{fieldKey.includes("hscodes") ? "Save" : "Bookmark"}</span>
                                        </span>
                                      ) : (
                                        <span className="tw-text-sm tw-text-primary-purple-600 tw-py-0.5 tw-px-2 tw-flex tw-items-center tw-border tw-rounded-lg tw-gap-0.5">
                                          {fieldKey.includes("hscodes") ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#5a1ce2">
                                              <path d="M816-672v456q0 29.7-21.15 50.85Q773.7-144 744-144H216q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h456l144 144ZM480-252q45 0 76.5-31.5T588-360q0-45-31.5-76.5T480-468q-45 0-76.5 31.5T372-360q0 45 31.5 76.5T480-252ZM264-552h336v-144H264v144Z" />
                                            </svg>
                                          ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="24px" fill="#5a1ce2">
                                              <path d="m233-120 65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Z" />
                                            </svg>
                                          )}
                                          <span className="tw-text-sm tw-text-primary-purple-600">{fieldKey.includes("hscodes") ? "Saved" : "Bookmarked"}</span>
                                        </span>
                                      )}
                                    </button>
                                    {item.isBookmarked && (
                                      <span
                                        className="tw-text-sm tw-text-gray-500 tw-py-0.5 tw-px-2 tw-flex tw-items-center tw-border tw-rounded-lg tw-cursor-pointer"
                                        onClick={() => {
                                          handleBookmark(fieldKey, item.key, item.isBookmarked);
                                        }}>
                                        <span className="material-symbols-outlined">delete</span>
                                        <span>Remove</span>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )
                    )}
                </div>

                {currentStep !== Object.keys(FORMORDER).length && <div className="tw-border-t-2 tw-border-gray-200"></div>}

                <div className={`tw-flex tw-items-center ${currentStep === PERSONA_INFO_STEP_INDEX || (BRAND.key === "source_atlas" && currentStep === 3) ? "tw-justify-end" : "tw-justify-between"}`}>
                  {currentStep !== PERSONA_INFO_STEP_INDEX && !(BRAND.key === "source_atlas" && currentStep === 3) && (
                    <Button size="medium" disabled={(!isBasicPlan && currentStep === 2) || currentStep === 1} className="tw-bg-white tw-text-gray-700 tw-text-sm tw-border tw-border-gray-300 tw-outline-none tw-rounded-lg tw-font-medium tw-px-6 tw-py-2 tw-transition-colors hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed" onClick={() => handleStepChange("prev")}>
                      Go Back
                    </Button>
                  )}

                  <button className="tw-bg-primary-purple-600 hover:tw-bg-primary-purple-700 tw-text-white tw-outline-none tw-border-none tw-rounded-lg tw-font-medium tw-px-6 tw-py-2 tw-transition-colors" onClick={() => handleStepChange("next")}>
                    {currentStep === Object.keys(FORMORDER).length ? "Continue to Search" : "Next"}
                  </button>
                </div>

                {currentStep === Object.keys(FORMORDER).length && (subBranchKey === LOB_ENUM.EXPORT || subBranchKey === LOB_ENUM.IMPORT || subBranchKey === LOB_ENUM.EXPORT_AND_IMPORT || subBranchKey === LOB_ENUM.BUSINESS_DEVELOPMENT || subBranchKey === LOB_ENUM.RESEARCH_AND_ANALYTICS) && (
                  <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-border tw-rounded-lg tw-border-[#7143f1] tw-bg-[#f6f3ff]">
                    <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-border tw-border-[#f6dd6e] tw-bg-[#fffef9] tw-rounded-lg">
                      <span className="tw-text-sm tw-font-medium">Reflection of these will take some time</span>
                    </div>
                    <div className="tw-flex tw-justify-between tw-gap-2">
                      <span
                        className="tw-py-1.5 tw-px-3 tw-border-gray-200 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-white tw-shadow-autoSuggest tw-cursor-pointer"
                        onClick={() => {
                          redirectToSidebar({
                            name: "Shipments",
                            link: "/search-shipments",
                            icon: <span className="material-symbols-outlined">youtube_searched_for</span>,
                            pendo_event: trackingEventDictionary.dashboardSearchHistory,
                            id: "shipments",
                          });
                          handleStepChange("next");
                        }}>
                        View Search Recommendations
                      </span>
                      {subBranchKey !== LOB_ENUM.BUSINESS_DEVELOPMENT && subBranchKey !== LOB_ENUM.RESEARCH_AND_ANALYTICS && subBranchKey !== LOB_ENUM.OTHER && (
                        <span
                          className="tw-py-1.5 tw-px-3 tw-border-gray-200 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-white tw-shadow-autoSuggest tw-cursor-pointer"
                          onClick={() => {
                            redirectToSidebar({
                              name: "Companies",
                              link: "/bookmarks/companies",
                              icon: <span className="material-symbols-outlined">factory</span>,
                              count: true,
                              count_key: "companies_count",
                              pendo_event: trackingEventDictionary.menuFavoriteCompanies,
                              id: "bookmarks_companies",
                            });
                            handleStepChange("next");
                          }}>
                          View Company Recommendations
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <Modal
          isOpen={showCloseConfirmationModal}
          onClose={() => {
            setShowCloseConfirmationModal(false);
            setCloseConfirmationStep(null);
          }}
          backdrop="static"
          size="sm">
          <Modal.Header>
            <span className="tw-text-sm tw-font-medium tw-text-gray-900">Confirmation</span>
          </Modal.Header>
          <Modal.Body>
            <div className="tw-text-sm tw-text-gray-900">Your onboarding preferences help us personalize insights based on your target markets, relevant companies and product lines. Are you sure you’d like to skip this step?</div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="tw-bg-white tw-text-gray-700 tw-text-sm tw-border tw-border-gray-300 tw-outline-none tw-rounded-lg tw-font-medium tw-px-4 tw-py-2"
              onClick={() => {
                setShowCloseConfirmationModal(false);
                setCloseConfirmationStep(null);
              }}>
              Cancel
            </button>
            <button type="button" className="tw-bg-primary-purple-600 hover:tw-bg-primary-purple-700 tw-text-white tw-text-sm tw-outline-none tw-border-none tw-rounded-lg tw-font-medium tw-px-4 tw-py-2 tw-ml-2" onClick={confirmCloseOnboardingPopup}>
              Confirm
            </button>
          </Modal.Footer>
        </Modal>

        {currentStep !== PERSONA_INFO_STEP_INDEX && (
          <span className="material-symbols-outlined tw-cursor-pointer tw-h-fit" onClick={handleCloseIconClick}>
            close
          </span>
        )}
      </div>
    </div>
  );
};

export default ProductOnboarding;
